import { unionBy } from 'lodash';

import { AuditTrialsListResponseWithFetchArgs } from './auditFilters.types';
import { AuditTrialType } from '@dynamika-shared/types/auditTrial';
import { shortDateUS, timeSec } from '@dynamika-shared/utils/date';
import { FilterDataType } from './auditFilters.slice';

type AuditDate = {
  dateAndTime: string;
};

export const SCORING_EVENT_NAMES = new Set([
  'DXA Eligibility',
  'Genant L1 Scoring From',
  'Genant Adjudication Scoring Form',
  'Genant L1 Scoring Form',
  'Reading task generated',
  'Combined Osteoarthritis Eligibility Scoring Form',
  'Reader receives token for DICOM',
  '11-Points Synovitis and ROAMES Eligibility Scoring Form',
  'Genant Vertebra Radiological Review Scoring Form',
  'Laterality Correction',
  'Subject added',
  'Image Series Split',
]);

export const auditTrailsFormatter = (
  audits: AuditTrialsListResponseWithFetchArgs | undefined,
  initialFilters: FilterDataType
) => {
  const modifiedAudits = audits?.content.reduce(
    (prev: AuditTrialType[] | [], curr: AuditTrialType): AuditTrialType[] => {
      const firstPayload = curr.payloads?.[0];
      const {
        subjectId: attributesSubjectId,
        visitDefinitionName,
        dxaVisitData: dxaData,
        visitName,
      } = curr.attributes;

      const statusOrVisitName =
        firstPayload?.entityData?.visitName ??
        firstPayload?.entityData?.uploadStatus;

      const actionTypeUpdateOrCreate =
        curr.actionType.toLowerCase() === 'update' ||
        curr.actionType.toLowerCase() === 'create';

      const hideFieldNameForDXAWorkStation =
        curr.event === 'DXA Files synchronized to GE/Hologic Workstation'
          ? 'N/A'
          : firstPayload?.fieldName;

      const showFieldNameForDXALUNARandHOLOGIC = dxaData?.length
        ? firstPayload?.fieldName
        : firstPayload?.newValue;

      const fieldBasedOnStatus = actionTypeUpdateOrCreate
        ? hideFieldNameForDXAWorkStation
        : statusOrVisitName;

      // NOTE: field
      const field = SCORING_EVENT_NAMES.has(curr.event)
        ? firstPayload?.entityName ?? 'Status'
        : fieldBasedOnStatus;

      const withoutScoring = actionTypeUpdateOrCreate
        ? showFieldNameForDXALUNARandHOLOGIC
        : firstPayload?.entityName;

      // NOTE: newValue
      const newValue = SCORING_EVENT_NAMES.has(curr.event)
        ? firstPayload?.entityData?.status ?? firstPayload?.newValue
        : withoutScoring;
      const oldValue = firstPayload?.oldValue;
      const subjectId = attributesSubjectId;

      const visitNameBasedOnDefinition =
        firstPayload?.entityData?.visitName ?? visitDefinitionName;
      // NOTE: visit
      const visit = SCORING_EVENT_NAMES.has(curr.event)
        ? visitName
        : visitNameBasedOnDefinition;
      const subjectCode = curr.payloads[0]?.entityData?.patientCode;

      const reasonForModification =
        SCORING_EVENT_NAMES.has(curr.event)
          ? firstPayload?.entityData?.scoring?.comment ?? ''
          : curr.reasonForModification ?? '';

      const date = shortDateUS(curr.dateAndTime);
      const time = timeSec(curr.dateAndTime);
      const dateAndTIme = `${date} ${time}`;
      const actionBy = curr.actionByName;
      const dxaVisitData = dxaData;

      return [
        ...prev,
        {
          ...curr,
          subjectId,
          field,
          oldValue,
          newValue,
          visit,
          active: !!audits?.patientId,
          dateAndTime: dateAndTIme,
          subjectCode,
          actionBy,
          dxaVisitData,
          reasonForModification,
          tags: curr.tags,
        },
      ];
    },
    []
  );

  const allFilterData = audits?.content.reduce((prev, curr) => {
    // TODO: unable this conditions when back will be ready to other filters
    // const firstPayload = curr.payloads?.[0];
    // const field =
    //   curr.actionType.toLowerCase() === 'update'
    //     ? firstPayload?.fieldName
    //     : firstPayload?.entityData?.visitName ||
    //       firstPayload?.entityData?.uploadStatus;
    // const newValue =
    //   curr.actionType.toLowerCase() === 'update'
    //     ? firstPayload?.newValue
    //     : firstPayload?.entityName;
    // const visit = firstPayload.entityData?.visitName || curr.attributes.visitDefinitionName;

    // TODO: unable this when back will be ready to other filters
    // const date = format(new Date(curr.dateAndTime), 'yyyy-LLL-dd');
    // const time = format(new Date(curr.dateAndTime), 'hh:mm:ss');
    // const dateAndTIme = `${date} ${time}`;

    if (curr.attributes.subjectId) {
      prev = {
        ...prev,
        subjectId: [
          ...prev.subjectId,
          {
            key: curr.attributes.subjectId,
            label: curr.attributes.subjectCode,
            value: curr.attributes.subjectId,
          },
        ],
      };
    }
    if (curr.event) {
      prev = {
        ...prev,
        event: [
          ...prev.event,
          { key: curr.event, label: curr.event, value: curr.event },
        ],
      };
    }

    return prev;
  }, initialFilters);

  // TODO: when back will be ready to accept another params
  // this variable should be moved to separate helper function
  // that should accept the array of desired fields,
  // for example subjectId, actionBy and etc.
  const uniqueSubjects = unionBy(allFilterData?.subjectId, 'key');
  const uniqueEvents = unionBy(allFilterData?.event, 'key');
  const withUniqueSubjects = {
    ...allFilterData,
    subjectId: uniqueSubjects,
    event: uniqueEvents,
  };

  // NOTE: unable this code when filtering will be added on the back side
  // or by demand.
  const filtered = audits?.patientId
    ? modifiedAudits?.filter(
        (sub: AuditTrialType) => sub.subjectId === audits?.patientId
      )
    : modifiedAudits;

  const sortedAuditsByDate = filtered?.sort(
    (date: AuditDate, latestDate: AuditDate) =>
      new Date(latestDate.dateAndTime).getTime() -
      new Date(date.dateAndTime).getTime()
  );

  return {
    loading: false,
    data: { ...audits, content: sortedAuditsByDate },
    allFilters: withUniqueSubjects,
  };
};
